/**
 * Embed context — which runtime the SPA is running in: `framed` (inside the
 * widget iframe), `paymentPopup` (the top-level pay window opened out of the
 * iframe for Adyen/3DS), `loginPopup` (the top-level first-party login window
 * opened out of the iframe), or `standalone` (a normal top-level document: the
 * marketplace app, or the embed bundle opened from a Themed Booking Link).
 * Detection is persisted in sessionStorage so it survives in-frame navigation
 * and the cross-origin payment redirect legs. Single source of truth — nothing
 * else should probe `window.self`/`top`/`opener`/`name` directly. The popup
 * window-name signals are transient (the checkout tracking session overwrites
 * `window.name` at import time), so `embedBoot` memoises them first thing in
 * the client entry.
 *
 * Orthogonal to the runtime is WHICH bundle the document is running: the
 * booking-widget SPA (the `embed` webpack entry) marks itself on boot
 * ({@link markEmbedBundle}); the containment policy asks {@link isEmbedBundle}.
 */

import type { HostMessage } from '@/helpers/embedMessages';

export type EmbedContext = 'framed' | 'paymentPopup' | 'loginPopup' | 'standalone';

/**
 * The merchant theme wire params and the sessionStorage keys embedEnv memoes
 * them under. Owned here, not in the booking-widget feature, because the shared
 * checkout reads them too (the Bokadirekt Pay return URL).
 */
export const EMBED_THEME_MEMO_KEYS = {
  'primary-color': 'bd-embed-primary-color',
  theme: 'bd-embed-theme',
  corners: 'bd-embed-corners',
  font: 'bd-embed-font',
} as const;

const EMBED_SESSION_KEY = 'bd-embed-mode';
const EMBED_POPUP_KEY = 'bd-embed-popup';
const EMBED_LOGIN_POPUP_KEY = 'bd-embed-login-popup';
// Popup window name + opener is a widget-only signal: both names are the random
// ids the frame assigns when it opens a popup (`embedPopup`).
const PAY_WINDOW_NAME_PATTERN = /^bd-payment-[0-9a-f]{16}$/;
const LOGIN_WINDOW_NAME_PATTERN = /^bd-login-[0-9a-f]{16}$/;
// In-memory fallback for the boot-time popup detection when sessionStorage is
// unavailable — the window-name signal is gone once the tracking session runs.
let paymentPopupMemo = false;
let loginPopupMemo = false;
// Same fallback for the framed flag: the boot URL's `?embed=true` is the only
// proof, and the widget is one document from boot to confirmation, so the memo
// carries it across every client-side navigation when storage is blocked.
let framedMemo = false;
// Module-level on purpose, never storage: the bundle is a property of the
// running script, so nothing can leak into a marketplace-SPA document opened
// later in the same tab. The embed entry's first import sets it, so no module
// in that bundle evaluates before it.
let embedBundle = false;

/**
 * The merchant theme as its wire params, for a URL that must boot a new
 * document in the same look — the Bokadirekt Pay return URL, which an app-switch
 * payment can open in a fresh tab with no memos. Unset params are omitted.
 */
export function getEmbedThemeParams(): Record<string, string> {
  const params: Record<string, string> = {};
  Object.entries(EMBED_THEME_MEMO_KEYS).forEach(([param, key]) => {
    try {
      const value = sessionStorage.getItem(key);
      if (value) params[param] = value;
    } catch {
      /* sessionStorage may be unavailable in a partitioned/blocked context */
    }
  });
  return params;
}

/** Mark this document as the booking-widget SPA. Called by the embed entry's first import (`embedBundleBoot`). */
export function markEmbedBundle(): void {
  embedBundle = true;
}

/** True when the running script is the booking-widget SPA (the `embed` entry), not the marketplace SPA. */
export function isEmbedBundle(): boolean {
  return embedBundle;
}

/**
 * Prefix a site-root URL with the embed router's basename when the running
 * script is the booking-widget SPA, so a URL the browser navigates to comes
 * back into this SPA instead of the marketplace one. `siteBaseUrl` must end in
 * a slash, as both `config.baseUrl` and `url.getBaseUrl()` do.
 *
 * The bundle mark decides rather than the router basename because the callers —
 * the Bokadirekt Pay return and reconcile URL builders — are plain functions
 * with no router access.
 */
export function withEmbedBasename(siteBaseUrl: string): string {
  return embedBundle ? `${siteBaseUrl}embed/` : siteBaseUrl;
}

/**
 * True whenever this document sits in any frame. Unlike {@link isEmbedMode},
 * which needs the loader's `embed=true` on the boot URL, this is the browser's
 * own answer: a framing page cannot make it false.
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws → we are framed.
    return true;
  }
}

// Memoised via sessionStorage (in memory when that is blocked) so it is stable
// across in-frame navigation.
function isFramed(): boolean {
  if (framedMemo) return true;
  try {
    if (sessionStorage.getItem(EMBED_SESSION_KEY) === 'true') return true;
  } catch {
    /* sessionStorage may be unavailable in a partitioned/blocked context */
  }

  const params = new URLSearchParams(window.location.search);
  if (params.get('embed') === 'true' && isInIframe()) {
    try {
      sessionStorage.setItem(EMBED_SESSION_KEY, 'true');
    } catch {
      framedMemo = true;
    }
    return true;
  }

  return false;
}

/**
 * Mark this window as the out-of-frame pay popup — called on popup boot, before
 * any cross-origin Adyen redirect can strip the transient signals.
 */
export function markPaymentPopup(): void {
  try {
    sessionStorage.setItem(EMBED_POPUP_KEY, 'true');
  } catch {
    paymentPopupMemo = true;
  }
}

// Requires a positive marker — never a bare `window.opener`, which any
// script-opened tab has.
function isPaymentPopup(): boolean {
  if (paymentPopupMemo) return true;
  try {
    if (sessionStorage.getItem(EMBED_POPUP_KEY) === 'true') return true;
  } catch {
    /* ignore */
  }

  // window.name survives only until a cross-origin redirect — persist immediately.
  if (window.opener && PAY_WINDOW_NAME_PATTERN.test(window.name)) {
    markPaymentPopup();
    return true;
  }

  return false;
}

/** Mark this window as the out-of-frame login popup — called on popup boot. */
export function markLoginPopup(): void {
  try {
    sessionStorage.setItem(EMBED_LOGIN_POPUP_KEY, 'true');
  } catch {
    loginPopupMemo = true;
  }
}

// Same positive-marker rule as the pay popup: memo, or opener + our window name.
function isLoginPopup(): boolean {
  if (loginPopupMemo) return true;
  try {
    if (sessionStorage.getItem(EMBED_LOGIN_POPUP_KEY) === 'true') return true;
  } catch {
    /* ignore */
  }

  if (window.opener && LOGIN_WINDOW_NAME_PATTERN.test(window.name)) {
    markLoginPopup();
    return true;
  }

  return false;
}

/**
 * The runtime context the SPA is in. SSR-safe. Order is load-bearing: both
 * popups are top-level, so check them before `framed`.
 */
export function getEmbedContext(): EmbedContext {
  if (typeof window === 'undefined') return 'standalone';
  if (isPaymentPopup()) return 'paymentPopup';
  if (isLoginPopup()) return 'loginPopup';
  if (isFramed()) return 'framed';
  return 'standalone';
}

/** True when the SPA is running inside the booking widget iframe. */
export function isEmbedMode(): boolean {
  return getEmbedContext() === 'framed';
}

/**
 * Post to the host: parent when framed, opener from the pay popup; no-op
 * otherwise (standalone, and the login popup — its same-origin handback goes
 * through `embedLogin`). Posts with `'*'` — the merchant origin is unknown
 * here; the host validates origin on receipt (see {@link parseHostMessage}).
 */
export function postToHost(message: HostMessage): void {
  switch (getEmbedContext()) {
    case 'framed':
      window.parent.postMessage(message, '*');
      break;
    case 'paymentPopup':
      window.opener?.postMessage(message, '*');
      break;
    default:
      break;
  }
}

/** Inside the iframe the host owns the top-level location — ask it to navigate. */
export function navigate(url: string): void {
  if (getEmbedContext() === 'framed') {
    postToHost({ type: 'BD_WIDGET_NAVIGATE', url });
  } else {
    window.location.href = url;
  }
}
